import React, { useState, useEffect } from "react";
import { Form, Button, Tag, Row, Col, AutoComplete, Alert, message, Input } from "antd";

import {
  getProductsServicesAutocomplete,
  postProduct,
  getRecommendedServices,
  getServices,
} from "../../../../../API/api";
import { Service, Suggested } from "../../config";
import searchIcon from "../../../../../webflow/images/search-icon.svg";
import { toggleDropdown } from "../../../../../utils/utils";

interface ServicesProps {
  data: any;
  setData: any;
}

export const Services: React.FC<ServicesProps> = ({ data, setData }: ServicesProps) => {
  const [activeModal, setActiveModal] = useState(false);
  const [selectIndustryOpened, setSelectIndustryOpened] = useState(false);
  const [valueAutoComplete, setValueAutoComplete] = useState("");
  const [suggestedServices, setSuggestedServices] = useState<Suggested[]>([]);
  const [listOfServices, setListOfServices] = useState([]);
  const [newServiceValue, setNewServiceValue] = useState("");
  const [originalListOfServices, setOriginalListOfServices] = useState<any[]>([]);
  const [alertText, setAlertText] = useState("") as any;
  const [industrySelected, setIndustrySelected] = useState<any>({
    name: "",
    id: "",
  });
  const isMandatory = data?.products?.length === 0 && data?.services?.length === 0;

  const getPlaceholderText = () => {
    let text = "";

    if (!data.industries[0]) {
      text = "First add industries";
    } else if (!industrySelected.id) {
      text = "Select an industry";
    } else if (listOfServices?.length) {
      text = "Search a service";
    } else {
      text = "No services related to the industry";
    }

    return text;
  };

  const industrySelectedName = industrySelected.name ? industrySelected.name : "Select an industry";

  const reset = () => {
    const resetObject = {
      services: [],
      serviceIds: [],
    };

    setData({ ...data, ...resetObject });
  };

  const addService = (service: any) => {
    const { services, serviceIds } = data;
    services.push(service.value);
    serviceIds.push(service.key);

    setData({ ...data, ...{ services, serviceIds } });
  };

  const removeService = (service: string) => {
    const { services, serviceIds } = data;
    const index = services.indexOf(service);
    serviceIds.splice(index, 1);

    const newData = {
      services: services.filter((item: string) => item !== service),
      serviceIds,
    };

    setData({ ...data, ...newData });
  };

  const handleSelectIndustry = async (ind: { name: string; id: string }) => {
    const services = await getServices(ind.id);
    setIndustrySelected(ind);
    setOriginalListOfServices(services);
    setListOfServices(services);
    setSelectIndustryOpened(false);
    setValueAutoComplete("");
  };

  const handleServiceSearch = (value: string) => {
    const res: any = [];

    originalListOfServices?.forEach((option) => {
      if (option.name) {
        /** Industry case */
        if (option.name.toLowerCase().indexOf(value.toLowerCase()) >= 0) {
          res.push(option);
        }
      } else if (option.toLowerCase().indexOf(value.toLowerCase()) >= 0) {
        /** Products or services case */
        res.push(option);
      }
    });

    setListOfServices(res);
  };

  const handleClickSuggestion = (suggestion: any) => {
    const idxOfSuggestion = suggestedServices.indexOf(suggestion);
    const suggesteds = [...suggestedServices];
    suggesteds.splice(idxOfSuggestion, 1, {
      id: suggestion.id,
      name: suggestion.name,
      selected: !suggesteds[idxOfSuggestion].selected,
    });
    if (suggesteds[idxOfSuggestion].selected) {
      addService({
        value: suggestion.name,
        key: suggestion.id,
      });
    } else {
      removeService(suggestion.name);
    }
    setSuggestedServices(suggesteds);
  };

  const addNewService = (serviceName: string) => {
    const newService = {
      subIndustryId: industrySelected.id,
      parentId: industrySelected.id,
      type: "service",
      name: serviceName,
    };
    try {
      postProduct(newService).then((response: any) => {
        if (response.status === 409) {
          setAlertText(response.message);
          setTimeout(() => {
            setAlertText("");
          }, 3000);
        } else if (response.status === 200) {
          addService({ key: response.data.id, value: response.data.name });
          handleSelectIndustry(industrySelected);
        }
      });
    } catch (error) {
      message.error("Error posting service", 2);
    }
  };

  useEffect(() => {
    if (industrySelected?.id) {
      (async () => {
        let services = await getRecommendedServices(industrySelected.id);
        services = services.map((pr: Suggested) => ({
          ...pr,
          selected: data?.serviceIds ? data.serviceIds.indexOf(pr.id) >= 0 : false,
        }));
        setSuggestedServices(services);
      })();
    } else if (suggestedServices?.length) {
      setSuggestedServices([]);
    }
  }, [industrySelected]);

  useEffect(() => {
    if (data?.industries?.length === 1) {
      handleSelectIndustry({ name: data.industries[0], id: data.industryIds[0] });
    }
  }, [data]);

  return (
    <>
      <div className="form_field-label">Services</div>
      <div className="selected-tags-block">
        {data?.services?.map((service: any, index: any) => {
          return (
            <Tag
              key={`serviceTag${index + 1}`}
              className="selected-tag"
              closable
              onClose={() => removeService(service)}
            >
              {service}
            </Tag>
          );
        })}
      </div>
      <Button className="btn-wrapper mgn-top-0-5em" onClick={() => setActiveModal(true)}>
        <a href="#" className="btn orange w-inline-block">
          <div>Add service</div>
          <div className="icon add-category-icon w-embed">
            <svg
              width="14"
              height="14"
              viewBox="0 0 10 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.88867 4.13574H9.45703V5.67383H5.88867V9.7168H4.25391V5.67383H0.685547V4.13574H4.25391V0.400391H5.88867V4.13574Z"
                fill="currentColor"
              />
            </svg>
          </div>
        </a>
      </Button>

      <Form.Item
        name="services"
        className="hiddenField"
        hidden
        rules={[
          {
            required: isMandatory,
            message: (
              <div className="form_error-text">
                <span className="error-icon" /> Please select at least one service
              </div>
            ),
          },
        ]}
      >
        <Input value={data?.services?.join(",")} />
      </Form.Item>

      <Row>
        <div
          className="blurred-bg industry-popup"
          style={{ display: activeModal ? "flex" : "none" }}
        >
          <div className="popup_c-block">
            <div className="form_field-label btm-mgn-0-5em">Add services</div>
            <>
              {data?.industries?.length > 1 && (
                <Col
                  span={24}
                  data-hover="false"
                  data-delay="0"
                  className="category-dropdown fw w-dropdown"
                >
                  <div
                    className={`category-dropdown_toggle fw w-dropdown-toggle ${
                      data.industries.length === 1 ? "category-dropdown_list--disabled" : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      setSelectIndustryOpened(!selectIndustryOpened);
                      toggleDropdown(e.target);
                    }}
                    onKeyDown={(e) => {
                      setSelectIndustryOpened(!selectIndustryOpened);
                      toggleDropdown(e.target);
                    }}
                  >
                    <div className="w-icon-dropdown-toggle" />
                    <div>{!data.industries[0] ? "First add industries" : industrySelectedName}</div>
                  </div>
                  <nav
                    className={`category-dropdown_list w-dropdown-list ${
                      data.industries.length === 1 ? "category-dropdown_list--disabled" : ""
                    } ${selectIndustryOpened ? "w--open" : ""}`}
                  >
                    {data?.industries?.map((industry: string, index: number) => (
                      <a
                        key={`${industry}-${index.toString()}`}
                        role="button"
                        tabIndex={0}
                        className="category-dropdown_link w-dropdown-link"
                        onClick={() =>
                          handleSelectIndustry({ name: industry, id: data.industryIds[index] })
                        }
                        onKeyDown={() =>
                          handleSelectIndustry({ name: industry, id: data.industryIds[index] })
                        }
                      >
                        {industry}
                      </a>
                    ))}
                  </nav>
                </Col>
              )}
              <div className="search industry-search w-form">
                <Form.Item required={false} validateTrigger={["onChange", "onBlur"]} noStyle>
                  <div className="search-wrapper">
                    <AutoComplete
                      onSelect={(value, instance) => {
                        addService(instance);
                      }}
                      className="search-input pad w-input"
                      onSearch={handleServiceSearch}
                      placeholder={getPlaceholderText()}
                      style={{ width: 500 }}
                      onChange={(input: string) => {
                        setValueAutoComplete(input);
                        getProductsServicesAutocomplete(
                          {
                            success: (response: []) => {
                              if (response.length) {
                                setListOfServices(response);
                              }
                            },
                            error: (error: any) => {
                              message.error(error.message, 2);
                            },
                          },
                          { value: input, type: "service", subIndustryId: industrySelected.id }
                        );
                      }}
                      value={valueAutoComplete}
                      disabled={
                        !data.industries[0] ||
                        !industrySelected.id ||
                        !originalListOfServices?.length
                      }
                    >
                      {listOfServices?.map((option: Service) => (
                        <AutoComplete.Option key={option.id} value={option.name}>
                          {option.name}
                        </AutoComplete.Option>
                      ))}
                    </AutoComplete>
                    <img src={searchIcon} loading="lazy" className="search-icon" alt="search" />
                  </div>
                </Form.Item>
              </div>
              {suggestedServices?.length > 0 && (
                <div className="popular-suggestions">
                  <div className="w-form">
                    <div className="form_field-label btm-mgn-0-5em">Suggested services:</div>

                    <div className="grid-2x">
                      {suggestedServices?.map((suggestion: Suggested, index: number) => {
                        return (
                          <label
                            key={`${suggestion}-${index.toString()}`}
                            className="w-checkbox industry-suggestions_checkbox"
                          >
                            <div
                              className={`w-checkbox-input w-checkbox-input--inputType-custom checkbox ${
                                suggestion.selected ? "w--redirected-checked" : ""
                              }`}
                            />
                            <input
                              type="checkbox"
                              id="checkbox"
                              name="checkbox"
                              data-name="Checkbox"
                              style={{ opacity: 0, position: "absolute", zIndex: -1 }}
                              onClick={() => handleClickSuggestion(suggestion)}
                            />
                            <span className="w-form-label">{suggestion.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              <div className="selected-tags-block">
                {data?.services?.map((service: any, index: any) => {
                  return (
                    <Tag
                      key={`serviceTag${index + 1}`}
                      className="selected-tag"
                      closable
                      onClose={() => removeService(service)}
                    >
                      {service}
                    </Tag>
                  );
                })}
              </div>

              <div className="not-found-block">
                <div className="form_field-label btm-mgn-0-5em">
                  Service not found? Please add here:
                </div>
                <div className="w-form">
                  <div className="form_field-wrap--flex">
                    <div className="search-wrapper">
                      <input
                        type="text"
                        className="search-input pad w-input"
                        maxLength={256}
                        name="Add-service"
                        disabled={!data.industries[0] || !industrySelected.name}
                        data-name="Add service"
                        placeholder={getPlaceholderText()}
                        id="Add-service"
                        onChange={(e) => setNewServiceValue(e.target.value)}
                        value={newServiceValue}
                        onKeyDown={(e: any) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation();
                            addNewService(newServiceValue);
                            setNewServiceValue("");
                          }
                        }}
                      />
                      <input
                        type="submit"
                        disabled={!data.industries[0] || !industrySelected.name}
                        value="Add"
                        className="search-button w-button"
                        onClick={(e: any) => {
                          e.preventDefault();
                          e.stopPropagation();
                          addNewService(newServiceValue);
                          setNewServiceValue("");
                        }}
                      />
                    </div>
                  </div>
                  {!!alertText && (
                    <Alert style={{ marginTop: "15px" }} message={alertText} type="error" />
                  )}
                </div>
              </div>
            </>

            <div className="reset-block">
              <div
                className="reset-suggestions cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={reset}
                onKeyDown={reset}
              >
                Reset
              </div>
              <div
                className="btn orange w-button cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => setActiveModal(false)}
                onKeyDown={() => setActiveModal(false)}
              >
                Ok
              </div>
            </div>
            <div
              role="button"
              tabIndex={0}
              data-w-id="88dbabb2-e4f5-b6cd-2422-cd4d8e59c2d6"
              className="modal-close"
              onClick={() => setActiveModal(false)}
              onKeyDown={() => setActiveModal(false)}
            >
              <div className="icon w-embed">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.75736 4.75736L13.2426 13.2426"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeMiterlimit="10"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.0729 4.58765L4.58765 13.0729"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeMiterlimit="10"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </Row>
    </>
  );
};
